"use client";

import { ReactNode, useMemo } from "react";
import { ConvexReactClient } from "convex/react";
import {
  ConvexBetterAuthProvider,
  type AuthClient,
} from "@convex-dev/better-auth/react";
import { authClient } from "@/lib/convex/auth-client";

/**
 * Convex + Better Auth provider. Coexists with TRPCProvider during the
 * strangler-fig migration (docs/CONVEX_MIGRATION_PLAN.md); tRPC goes away
 * once the last router is ported.
 */
export function ConvexClientProvider({ children }: { children: ReactNode }) {
  const convex = useMemo(() => {
    const url = process.env.NEXT_PUBLIC_CONVEX_URL;
    if (!url) return null;
    return new ConvexReactClient(url);
  }, []);

  // Convex not configured (e.g. preview env without the var) — render app
  // without Convex rather than crashing; Convex-powered pages will show
  // their own "not configured" state.
  if (!convex) return <>{children}</>;

  return (
    <ConvexBetterAuthProvider
      client={convex}
      // better-auth's inferred client generics don't structurally match the
      // component's AuthClient union even on identical versions; safe cast.
      authClient={authClient as unknown as AuthClient}
    >
      {children}
    </ConvexBetterAuthProvider>
  );
}
